import { useState, useRef, useEffect } from 'react';
import { Divider, Searchbar, TInputProps, TOption, TOptionValue, TSearchbarHandle } from '@treezor/design-system';

import { SelectFilter } from './SelectFilter';
import { ISearch } from './filters.type';

type TSearchFilterProps = Partial<Omit<TInputProps, 'onChange' | 'value'>> & {
  onChange: (value: ISearch) => void;
  value?: ISearch;
  options?: TOption[];
  initialValue?: TOptionValue;
};

export function SearchFilter({ onChange, value, options = [], initialValue, ...rest }: TSearchFilterProps) {
  const [selected, setSelected] = useState<TOptionValue>(initialValue || options[0]?.value);
  const inputRef = useRef<TSearchbarHandle | undefined>(undefined);

  useEffect(() => {
    if (!value && inputRef?.current) {
      // reset the input field
      inputRef.current.reset();
    }
    if (value?.value && (value.value !== inputRef.current.value || value.key !== selected)) {
      setSelected(value.key);
      inputRef.current.setValue(value.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Searchbar
      size="s"
      type="text"
      withSearchButton
      onChange={(searchValue: string) => {
        onChange({ key: selected.toString(), value: searchValue });
      }}
      ref={inputRef}
      icon={{
        left: (
          <div className="flex items-center gap-3">
            <SelectFilter
              isFilled={false}
              field={{
                options,
                value: options.find((option) => option.value === selected),
                handleChange: (value) => setSelected(value as TOptionValue),
              }}
              testId={`${rest['data-testid'] ? `${rest['data-testid']}-` : ''}search-filter-select`}
              className="border-none bg-transparent !px-0 !py-0 hover:bg-transparent [&>span:first-child]:gap-0"
            />
            <Divider className="min-h-5" direction="vertical" />
          </div>
        ),
      }}
      defaultValue={value?.value}
      {...rest}
      name={'search'}
      testId={`${rest['data-testid'] ? `${rest['data-testid']}-` : ''}search-filter`}
    />
  );
}
